import React, { useState } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { makeStyles } from "@material-ui/core/styles";
import { 
  Paper, 
  Typography, 
  IconButton, 
  Menu,
  MenuItem,
  Divider
} from "@material-ui/core";
import { 
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon 
} from "@material-ui/icons";
import { toast } from "react-toastify";

import KanbanCard from "./KanbanCard";
import NewCardDialog from "./NewCardDialog";
import EditStageDialog from "./EditStageDialog";
import ConfirmationDialog from "../ConfirmationDialog";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  stageContainer: {
    minWidth: 280,
    maxWidth: 280,
    height: "100%",
    margin: theme.spacing(0, 1),
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.default
  },
  stageHeader: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  stageTitle: {
    fontWeight: 500,
    display: "flex",
    alignItems: "center"
  },
  cardCount: {
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    borderRadius: "50%",
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem"
  },
  cardList: {
    padding: theme.spacing(1),
    overflowY: "auto",
    flexGrow: 1
  },
  addCardArea: {
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "center"
  },
  addCardButton: {
    width: "100%",
    textAlign: "left"
  }
}));

const KanbanStage = ({ stage, cards, index, onCardUpdate, onStageUpdate }) => {
  const classes = useStyles();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [newCardOpen, setNewCardOpen] = useState(false);
  const [editStageOpen, setEditStageOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    handleMenuClose();
    setEditStageOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteConfirmOpen(true);
  };

  const handleEditStageClose = () => {
    setEditStageOpen(false);
    if (onStageUpdate) onStageUpdate();
  };

  const handleNewCardOpen = () => {
    setNewCardOpen(true);
  };

  const handleNewCardClose = () => {
    setNewCardOpen(false);
    if (onCardUpdate) onCardUpdate();
  };

  const handleStageDelete = async () => {
    try {
      await api.delete(`/kanban/stages/${stage.id}`);
      
      toast.success(i18n.t("kanban.notifications.stageDeleted"));
      
      if (onStageUpdate) onStageUpdate();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.stageDeleteError"));
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  return (
    <Draggable draggableId={`stage-${stage.id}`} index={index} type="stage">
      {(provided) => (
        <Paper
          className={classes.stageContainer}
          {...provided.draggableProps}
          ref={provided.innerRef}
        >
          <div 
            className={classes.stageHeader}
            {...provided.dragHandleProps}
          >
            <Typography className={classes.stageTitle} variant="subtitle1">
              {stage.name}
              <span className={classes.cardCount}>{cards.length}</span>
            </Typography>
            <IconButton 
              size="small" 
              onClick={handleMenuOpen}
              aria-label="more"
              aria-controls="stage-menu"
              aria-haspopup="true"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              id="stage-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleEditClick}>
                <EditIcon fontSize="small" style={{ marginRight: 8 }} />
                {i18n.t("kanban.actions.editStage")}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleDeleteClick}>
                <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
                {i18n.t("kanban.actions.deleteStage")}
              </MenuItem>
            </Menu>
          </div>

          <Droppable droppableId={`stage-${stage.id}`} type="card">
            {(provided, snapshot) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  backgroundColor: snapshot.isDraggingOver
                    ? "rgba(0, 0, 0, 0.05)"
                    : "inherit"
                }}
              >
                {cards.map((card, index) => (
                  <KanbanCard
                    key={card.id}
                    card={card}
                    index={index}
                    onUpdate={onCardUpdate}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          <div className={classes.addCardArea}>
            <Typography 
              color="primary" 
              variant="body2" 
              onClick={handleNewCardOpen}
              style={{ cursor: "pointer" }}
            >
              + {i18n.t("kanban.actions.addCard")}
            </Typography>
          </div>

          <NewCardDialog
            open={newCardOpen}
            onClose={handleNewCardClose}
            stageId={stage.id}
          />

          <EditStageDialog
            open={editStageOpen}
            onClose={handleEditStageClose}
            stage={stage}
          />

          <ConfirmationDialog
            open={deleteConfirmOpen}
            onClose={() => setDeleteConfirmOpen(false)}
            onConfirm={handleStageDelete}
            title={i18n.t("kanban.dialogs.deleteStage.title")}
            message={i18n.t("kanban.dialogs.deleteStage.message")}
          />
        </Paper>
      )}
    </Draggable>
  );
};

export default KanbanStage;